import React, { useCallback, useEffect, useState } from "react";
import { api, extractError } from "@/lib/api";

const VETTING_OPTIONS = [
  "pending_verification",
  "manual_approval",
  "auto_listing",
  "suspended",
];

const VETTING_COLORS = {
  pending_verification: "bg-gray-200 text-gray-700",
  manual_approval: "bg-yellow-100 text-yellow-800",
  auto_listing: "bg-green-100 text-green-800",
  suspended: "bg-red-100 text-red-700",
};

export default function AdminPage() {
  const [settings, setSettings] = useState(null);
  const [users, setUsers] = useState([]);
  const [cases, setCases] = useState([]);
  const [mediators, setMediators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState(null);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, u, c, m] = await Promise.all([
        api.get("/admin/settings"),
        api.get("/admin/users"),
        api.get("/admin/cases"),
        api.get("/admin/mediators"),
      ]);
      setSettings(s.data);
      setUsers(u.data);
      setCases(c.data);
      setMediators(m.data);
    } catch (e) {
      setError(extractError(e, "Could not load admin data"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const updateSetting = async (key, value) => {
    setSavingKey(key);
    setError("");
    setSuccess("");
    try {
      const { data } = await api.put("/admin/settings", { [key]: value });
      setSettings(data);
      setSuccess("Settings updated.");
      setTimeout(() => setSuccess(""), 2500);
    } catch (e) {
      setError(extractError(e, "Could not update settings"));
    } finally {
      setSavingKey(null);
    }
  };

  const updateMediator = async (userId, patch) => {
    setSavingKey(`mediator-${userId}`);
    try {
      await api.put(`/admin/mediators/${userId}`, patch);
      await load();
    } catch (e) {
      setError(extractError(e, "Could not update mediator"));
    } finally {
      setSavingKey(null);
    }
  };

  const updateUserTier = async (userId, tier) => {
    setSavingKey(`user-tier-${userId}`);
    setError("");
    setSuccess("");
    try {
      await api.put(`/admin/users/${userId}`, { tier });
      await load();
      setSuccess("User tier updated.");
      setTimeout(() => setSuccess(""), 2500);
    } catch (e) {
      setError(extractError(e, "Could not update tier"));
    } finally {
      setSavingKey(null);
    }
  };

  // User-activity filter (client-side; CSV export mirrors it via query param).
  const [activityFilter, setActivityFilter] = useState("all"); // "all" | "7d" | "30d" | "signup_30d"
  const [nudgeInfo, setNudgeInfo] = useState({ count: 0, cooldown_days: 7, loaded: false });
  const [nudging, setNudging] = useState(false);
  const loadNudgeInfo = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/users/nudgeable");
      setNudgeInfo({ count: data.count, cooldown_days: data.cooldown_days, loaded: true });
    } catch (err) { console.warn("Could not load nudgeable count:", err); }
  }, []);
  useEffect(() => {
    loadNudgeInfo();
  }, [loadNudgeInfo]);
  const nudgeInactive = async () => {
    if (nudging || nudgeInfo.count === 0) return;
    if (!window.confirm(`Send a reminder email to ${nudgeInfo.count} account${nudgeInfo.count === 1 ? "" : "s"} who signed up in the last 30 days and haven't logged in yet?`)) return;
    setNudging(true);
    setError("");
    setSuccess("");
    try {
      const { data } = await api.post("/admin/users/nudge-inactive");
      setSuccess(`Nudged ${data.sent} of ${data.candidates} (${data.failed} failed).`);
      setTimeout(() => setSuccess(""), 4000);
      await loadNudgeInfo();
      await load();
    } catch (e) {
      setError(extractError(e, "Could not send nudges"));
    } finally {
      setNudging(false);
    }
  };
  const filteredUsers = users.filter((u) => {
    if (activityFilter === "all") return true;
    if (activityFilter === "signup_30d") {
      if (!u.created_at) return false;
      const cutoff = Date.now() - 30 * 24 * 60 * 60 * 1000;
      const ts = new Date(u.created_at).getTime();
      return Number.isFinite(ts) && ts >= cutoff;
    }
    if (!u.last_login_at) return false;
    const days = activityFilter === "7d" ? 7 : 30;
    const cutoff = Date.now() - days * 24 * 60 * 60 * 1000;
    const ts = new Date(u.last_login_at).getTime();
    return Number.isFinite(ts) && ts >= cutoff;
  });
  const exportCsvHref =
    activityFilter === "all"
      ? `${api.defaults.baseURL}/admin/users/export.csv`
      : `${api.defaults.baseURL}/admin/users/export.csv?active_within=${activityFilter}`;

  if (loading || !settings) {
    return (
      <div className="min-h-[40vh] flex items-center justify-center text-[color:var(--text-muted)]" data-testid="admin-loading">
        Loading admin…
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-6 py-12 fade-in" data-testid="admin-page">
      <div className="flex items-end justify-between mb-8">
        <div>
          <p className="text-xs uppercase tracking-[0.18em] text-[color:var(--participant-a)] font-medium">
            Admin
          </p>
          <h1 className="font-serif text-3xl md:text-4xl mt-1">Configuration & moderation</h1>
        </div>
        {success && (
          <div className="text-xs text-green-700 bg-green-50 border border-green-200 rounded-full px-3 py-1" data-testid="admin-success">
            {success}
          </div>
        )}
      </div>

      {error && (
        <div className="mb-4 text-sm text-[color:var(--counter-error)]" data-testid="admin-error">
          {error}
        </div>
      )}

      {/* Settings */}
      <section className="rounded-2xl border border-[color:var(--line)] bg-white p-6 mb-8" data-testid="settings-section">
        <h2 className="font-serif text-2xl mb-1">Business & compliance settings</h2>
        <p className="text-sm text-[color:var(--text-muted)] mb-5">
          These decisions are deferred and remain configurable. Default is
          permissive-safe until a final policy is set.
        </p>

        <div className="space-y-5">
          {/* Guardian consent - Hybrid policy */}
          <div data-testid="guardian-consent-row" className="space-y-3">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <div className="font-medium">
                  Guardian consent for ages 13–17 ·{" "}
                  <span className="text-[color:var(--participant-a)]">Hybrid policy</span>
                </div>
                <p className="text-xs text-[color:var(--text-muted)] max-w-xl mt-1">
                  Free-tier usage is permitted without consent. Paid upgrades,
                  professional-mediator involvement, and inviting outside
                  adults require a guardian to grant consent via a one-time
                  link. Each lever below can be toggled independently.
                </p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() =>
                    updateSetting(
                      "guardian_consent_required",
                      !settings.guardian_consent_required,
                    )
                  }
                  className={`px-3 py-1.5 rounded-full text-xs font-medium border ${
                    settings.guardian_consent_required
                      ? "bg-[color:var(--participant-a)] text-white border-transparent"
                      : "bg-white text-[color:var(--text-primary)] border-[color:var(--line)]"
                  }`}
                  disabled={savingKey === "guardian_consent_required"}
                  data-testid="toggle-guardian-required"
                >
                  General use: {settings.guardian_consent_required ? "Required" : "Not required"}
                </button>
                <button
                  onClick={() =>
                    updateSetting(
                      "guardian_consent_decided",
                      !settings.guardian_consent_decided,
                    )
                  }
                  className={`px-3 py-1.5 rounded-full text-xs font-medium border ${
                    settings.guardian_consent_decided
                      ? "bg-green-100 text-green-800 border-green-200"
                      : "bg-yellow-50 text-yellow-800 border-yellow-200"
                  }`}
                  disabled={savingKey === "guardian_consent_decided"}
                  data-testid="toggle-guardian-decided"
                >
                  {settings.guardian_consent_decided ? "Policy decided" : "Policy pending"}
                </button>
              </div>
            </div>

            <div className="grid sm:grid-cols-3 gap-2">
              <HybridConsentToggle
                label="Paid upgrades"
                tone="Money/financial action"
                value={settings.consent_required_for_payment}
                onChange={(v) => updateSetting("consent_required_for_payment", v)}
                saving={savingKey === "consent_required_for_payment"}
                testid="toggle-consent-payment"
              />
              <HybridConsentToggle
                label="Professional mediator"
                tone="Outside adult involvement"
                value={settings.consent_required_for_professional_mediator}
                onChange={(v) =>
                  updateSetting("consent_required_for_professional_mediator", v)
                }
                saving={savingKey === "consent_required_for_professional_mediator"}
                testid="toggle-consent-mediator"
              />
              <HybridConsentToggle
                label="Invite outside adult"
                tone="Inviting a non-school adult"
                value={settings.consent_required_for_external_invite}
                onChange={(v) =>
                  updateSetting("consent_required_for_external_invite", v)
                }
                saving={savingKey === "consent_required_for_external_invite"}
                testid="toggle-consent-invite"
              />
            </div>
          </div>

          {/* Mediator vetting default */}
          <div className="flex items-start justify-between gap-4 flex-wrap" data-testid="vetting-default-row">
            <div>
              <div className="font-medium">Default professional mediator vetting</div>
              <p className="text-xs text-[color:var(--text-muted)] max-w-xl mt-1">
                Newly registered professional mediators start in this state
                until the final vetting policy is in place.
              </p>
            </div>
            <select
              value={settings.default_mediator_vetting}
              onChange={(e) =>
                updateSetting("default_mediator_vetting", e.target.value)
              }
              disabled={savingKey === "default_mediator_vetting"}
              className="px-3 py-2 rounded-xl border border-[color:var(--line)] bg-white text-sm"
              data-testid="default-vetting-select"
            >
              {VETTING_OPTIONS.map((v) => (
                <option key={v} value={v}>
                  {v.replace(/_/g, " ")}
                </option>
              ))}
            </select>
          </div>

          {/* Payment provider */}
          <div className="flex items-start justify-between gap-4 flex-wrap" data-testid="payment-provider-row">
            <div>
              <div className="font-medium">Payment provider</div>
              <p className="text-xs text-[color:var(--text-muted)] max-w-xl mt-1">
                Architecture supports any provider. The current value is a
                placeholder — switch to Stripe, PayPal, PayFast or Paystack
                once finalised.
              </p>
            </div>
            <select
              value={settings.payment_provider}
              onChange={(e) => updateSetting("payment_provider", e.target.value)}
              disabled={savingKey === "payment_provider"}
              className="px-3 py-2 rounded-xl border border-[color:var(--line)] bg-white text-sm"
              data-testid="payment-provider-select"
            >
              <option value="placeholder">placeholder</option>
              <option value="stripe">stripe</option>
              <option value="paypal">paypal</option>
              <option value="payfast">payfast</option>
              <option value="paystack">paystack</option>
            </select>
          </div>
        </div>
      </section>

      {/* Mediators */}
      <section className="rounded-2xl border border-[color:var(--line)] bg-white p-6 mb-8" data-testid="mediators-section">
        <h2 className="font-serif text-2xl mb-1">Professional mediators</h2>
        <p className="text-sm text-[color:var(--text-muted)] mb-3">
          Mediators must be registered with the <b>national regulator</b> of
          the country where at least one party is legally resident. Capture
          each mediator's regulator, country, and registration number below.
        </p>
        {mediators.length === 0 ? (
          <p className="text-sm text-[color:var(--text-muted)]" data-testid="mediators-empty">
            No professional mediators yet.
          </p>
        ) : (
          <ul className="divide-y divide-[color:var(--line)]">
            {mediators.map((m) => {
              const status = m.mediator_vetting || settings.default_mediator_vetting;
              return (
                <li
                  key={m.id}
                  className="py-4"
                  data-testid={`mediator-row-${m.id}`}
                >
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div>
                      <div className="font-medium">{m.name || m.email}</div>
                      <div className="text-xs text-[color:var(--text-muted)]">{m.email}</div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span
                        className={`text-xs px-2 py-1 rounded-full ${VETTING_COLORS[status]}`}
                        data-testid={`mediator-status-${m.id}`}
                      >
                        {status.replace(/_/g, " ")}
                      </span>
                      <select
                        value={status}
                        onChange={(e) =>
                          updateMediator(m.id, { vetting_status: e.target.value })
                        }
                        className="px-2 py-1 rounded-md border border-[color:var(--line)] text-xs bg-white"
                        data-testid={`mediator-select-${m.id}`}
                      >
                        {VETTING_OPTIONS.map((v) => (
                          <option key={v} value={v}>
                            {v.replace(/_/g, " ")}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <MediatorRegulatorForm
                    mediator={m}
                    onSave={(patch) => updateMediator(m.id, patch)}
                    saving={savingKey === `mediator-${m.id}`}
                  />
                </li>
              );
            })}
          </ul>
        )}
      </section>

      {/* Stats */}
      <section className="grid md:grid-cols-2 gap-4">
        <div className="rounded-2xl border border-[color:var(--line)] bg-white p-6" data-testid="stats-users">
          <div className="text-xs uppercase tracking-wider text-[color:var(--text-muted)]">Users</div>
          <div className="font-serif text-4xl mt-1">{users.length}</div>
        </div>
        <div className="rounded-2xl border border-[color:var(--line)] bg-white p-6" data-testid="stats-cases">
          <div className="text-xs uppercase tracking-wider text-[color:var(--text-muted)]">Active cases</div>
          <div className="font-serif text-4xl mt-1">{cases.length}</div>
        </div>
      </section>

      {/* User activity — signup + last login snapshot with CSV export */}
      <section
        className="rounded-2xl border border-[color:var(--line)] bg-white p-6 mb-8"
        data-testid="admin-user-activity-section"
      >
        <div className="flex items-baseline justify-between gap-3 mb-3 flex-wrap">
          <div>
            <h2 className="font-serif text-2xl">User activity</h2>
            <p className="text-sm text-[color:var(--text-muted)]">
              Every registered account, when they signed up, and the last time they signed in.
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button
              type="button"
              onClick={nudgeInactive}
              disabled={nudging || !nudgeInfo.loaded || nudgeInfo.count === 0}
              data-testid="admin-nudge-inactive-btn"
              className="px-4 py-2 rounded-full border border-[color:var(--participant-a)] text-[color:var(--participant-a)] text-sm font-semibold hover:bg-[color:var(--participant-a)] hover:text-white transition disabled:opacity-40 disabled:cursor-not-allowed"
              title={
                nudgeInfo.count === 0
                  ? "No accounts to nudge right now — everyone has either logged in or been contacted recently."
                  : `Send a reminder to ${nudgeInfo.count} recent signup${nudgeInfo.count === 1 ? "" : "s"} who haven't logged in.`
              }
            >
              {nudging
                ? "Sending…"
                : `Nudge inactive (${nudgeInfo.count})`}
            </button>
            <a
              href={exportCsvHref}
              download
              className="px-4 py-2 rounded-full bg-[color:var(--participant-a)] text-white text-sm font-semibold hover:opacity-90"
              data-testid="admin-export-users-csv"
            >
              Export CSV
            </a>
          </div>
        </div>
        <div
          className="flex items-center gap-2 flex-wrap mb-4"
          data-testid="admin-activity-filters"
          role="tablist"
          aria-label="Activity filter"
        >
          {[
            { key: "all", label: "All users" },
            { key: "7d", label: "Active last 7 days" },
            { key: "30d", label: "Active last 30 days" },
            { key: "signup_30d", label: "New signups last 30 days" },
          ].map((chip) => {
            const active = activityFilter === chip.key;
            return (
              <button
                key={chip.key}
                type="button"
                onClick={() => setActivityFilter(chip.key)}
                role="tab"
                aria-selected={active}
                data-testid={`admin-activity-filter-${chip.key}`}
                className={
                  "px-3 py-1.5 rounded-full text-xs font-medium border transition " +
                  (active
                    ? "bg-[color:var(--participant-a)] text-white border-[color:var(--participant-a)]"
                    : "bg-white text-[color:var(--text-primary)] border-[color:var(--line)] hover:bg-[color:var(--surface-sage)]")
                }
              >
                {chip.label}
              </button>
            );
          })}
          <span
            className="text-xs text-[color:var(--text-muted)] ml-1"
            data-testid="admin-activity-count"
          >
            {filteredUsers.length} of {users.length}
          </span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="admin-user-activity-table">
            <thead className="text-xs uppercase tracking-wider text-[color:var(--text-muted)]">
              <tr>
                <th className="text-left py-2 pr-3">Email</th>
                <th className="text-left py-2 pr-3">Name</th>
                <th className="text-left py-2 pr-3">Signed up</th>
                <th className="text-left py-2 pr-3">Last login</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.length === 0 && (
                <tr>
                  <td
                    colSpan={4}
                    className="py-6 text-center text-[color:var(--text-muted)] text-sm italic"
                    data-testid="admin-activity-empty"
                  >
                    No users match this filter yet.
                  </td>
                </tr>
              )}
              {filteredUsers.map((u) => (
                <tr
                  key={`activity-${u.id}`}
                  className="border-t border-[color:var(--line)]"
                  data-testid={`admin-user-activity-row-${u.id}`}
                >
                  <td className="py-2 pr-3">
                    <div className="font-medium">{u.email}</div>
                    <div className="text-xs text-[color:var(--text-muted)]">{u.auth_provider || "email"}</div>
                  </td>
                  <td className="py-2 pr-3">{u.name || "—"}</td>
                  <td
                    className="py-2 pr-3 text-[color:var(--text-muted)]"
                    data-testid={`admin-user-signup-${u.id}`}
                  >
                    {u.created_at ? new Date(u.created_at).toLocaleString() : "—"}
                  </td>
                  <td
                    className="py-2 pr-3"
                    data-testid={`admin-user-last-login-${u.id}`}
                  >
                    {u.last_login_at ? (
                      new Date(u.last_login_at).toLocaleString()
                    ) : (
                      <span className="text-[color:var(--text-muted)] italic">Never</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* User tier management */}
      <section
        className="rounded-2xl border border-[color:var(--line)] bg-white p-6"
        data-testid="admin-users-section"
      >
        <div className="flex items-baseline justify-between gap-3 mb-3">
          <h2 className="font-serif text-2xl">Users & tiers</h2>
          <span className="text-xs text-[color:var(--text-muted)]">
            Promote or demote a user's plan tier. Takes effect on the next case they open.
          </span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="admin-users-table">
            <thead className="text-xs uppercase tracking-wider text-[color:var(--text-muted)]">
              <tr>
                <th className="text-left py-2 pr-3">User</th>
                <th className="text-left py-2 pr-3">Role</th>
                <th className="text-left py-2 pr-3">Current tier</th>
                <th className="text-left py-2 pr-3">Change tier</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr
                  key={u.id}
                  className="border-t border-[color:var(--line)]"
                  data-testid={`admin-user-row-${u.id}`}
                >
                  <td className="py-2 pr-3">
                    <div className="font-medium">{u.name || u.email}</div>
                    <div className="text-xs text-[color:var(--text-muted)]">{u.email}</div>
                  </td>
                  <td className="py-2 pr-3">{u.role || "user"}</td>
                  <td className="py-2 pr-3">
                    <span
                      className="inline-block text-xs px-2 py-0.5 rounded-full bg-[color:var(--surface-sage)] capitalize"
                      data-testid={`admin-user-tier-${u.id}`}
                    >
                      {u.tier || "free"}
                    </span>
                  </td>
                  <td className="py-2 pr-3">
                    <select
                      value={u.tier || "free"}
                      disabled={u.role === "admin" || savingKey === `user-tier-${u.id}`}
                      onChange={(e) => updateUserTier(u.id, e.target.value)}
                      data-testid={`admin-user-tier-select-${u.id}`}
                      className="px-3 py-1.5 rounded-lg border border-[color:var(--line)] bg-white text-sm disabled:opacity-60"
                    >
                      <option value="free">Free</option>
                      <option value="basic">Basic</option>
                      <option value="premium">Premium</option>
                      <option value="professional">Professional</option>
                    </select>
                    {u.role === "admin" && (
                      <span className="ml-2 text-xs text-[color:var(--text-muted)]">
                        admins bypass tier
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

function MediatorRegulatorForm({ mediator, onSave, saving }) {
  const [country, setCountry] = useState(mediator.regulator_country || "");
  const [regulator, setRegulator] = useState(mediator.regulator_name || "");
  const [regNumber, setRegNumber] = useState(mediator.registration_number || "");

  const dirty =
    country !== (mediator.regulator_country || "") ||
    regulator !== (mediator.regulator_name || "") ||
    regNumber !== (mediator.registration_number || "");

  return (
    <div
      className="mt-3 grid grid-cols-1 md:grid-cols-4 gap-2 items-end"
      data-testid={`mediator-regulator-form-${mediator.id}`}
    >
      <label className="text-xs">
        <span className="block text-[color:var(--text-muted)] mb-1">Regulator country</span>
        <input
          value={country}
          onChange={(e) => setCountry(e.target.value)}
          placeholder="e.g. South Africa"
          className="w-full px-3 py-2 rounded-lg border border-[color:var(--line)] bg-white text-sm"
          data-testid={`mediator-country-${mediator.id}`}
        />
      </label>
      <label className="text-xs">
        <span className="block text-[color:var(--text-muted)] mb-1">National regulator</span>
        <input
          value={regulator}
          onChange={(e) => setRegulator(e.target.value)}
          placeholder="e.g. Board of Conciliators"
          className="w-full px-3 py-2 rounded-lg border border-[color:var(--line)] bg-white text-sm"
          data-testid={`mediator-regulator-${mediator.id}`}
        />
      </label>
      <label className="text-xs">
        <span className="block text-[color:var(--text-muted)] mb-1">Registration #</span>
        <input
          value={regNumber}
          onChange={(e) => setRegNumber(e.target.value)}
          placeholder="e.g. CON-2025-00123"
          className="w-full px-3 py-2 rounded-lg border border-[color:var(--line)] bg-white text-sm"
          data-testid={`mediator-regnumber-${mediator.id}`}
        />
      </label>
      <button
        type="button"
        onClick={() =>
          onSave({
            regulator_country: country,
            regulator_name: regulator,
            registration_number: regNumber,
          })
        }
        disabled={!dirty || saving}
        className="px-3 py-2 rounded-full bg-[color:var(--participant-a)] text-white text-xs font-semibold hover:opacity-90 disabled:opacity-50"
        data-testid={`mediator-save-${mediator.id}`}
      >
        {saving ? "Saving..." : "Save regulator info"}
      </button>
    </div>
  );
}



function HybridConsentToggle({ label, tone, value, onChange, saving, testid }) {
  const enabled = value !== false; // default true when undefined
  return (
    <button
      type="button"
      onClick={() => onChange(!enabled)}
      disabled={saving}
      data-testid={testid}
      className={`text-left rounded-xl border p-3 transition ${
        enabled
          ? "border-[color:var(--participant-a)]/30 bg-white"
          : "border-[color:var(--line)] bg-[color:var(--bg-base)]"
      }`}
    >
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm font-medium">{label}</span>
        <span
          className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full ${
            enabled
              ? "bg-[color:var(--participant-a)] text-white"
              : "bg-white text-[color:var(--text-muted)] border border-[color:var(--line)]"
          }`}
        >
          {enabled ? "Consent required" : "No consent needed"}
        </span>
      </div>
      <div className="text-[11px] text-[color:var(--text-muted)] mt-1">{tone}</div>
    </button>
  );
}
